import React from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { webhooksKey } from '@dashboard/lib/queryKeys';
import { WEBHOOK_EVENT, WEBHOOK_STATUS } from '@dashboard/enums/webhook-events';
import { eventFieldName, selectedWebhookEvents } from './webhookEventFields';

const WebhookEditDialog = ({ open, onClose, webhook }) => {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();

  const updateWebhook = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${sellerId}/webhooks/${webhook.id}`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhooksKey(sellerId) });
      toast({
        title: 'Webhook updated',
        type: 'success',
      });
      onClose();
    },
    onError: error => {
      toast({
        title: 'Failed to update webhook',
        description: error?.message || 'Please check your configuration and try again',
        type: 'error',
      });
    },
  });

  const eventOptions = Object.entries(WEBHOOK_EVENT).map(([_key, value]) => ({
    value,
    label: value,
  }));

  const statusOptions = Object.entries(WEBHOOK_STATUS)
    .filter(([key]) => key !== 'FAILED')
    .map(([_key, value]) => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1),
    }));

  if (!webhook) return null;

  // Format custom headers for display
  const formatCustomHeaders = headers => {
    if (!headers || Object.keys(headers).length === 0) return '';
    return JSON.stringify(headers, null, 2);
  };

  return (
    <Dialog
      title="Edit webhook"
      cancelButton={null}
      confirmButton={null}
      size="xl"
      open={open}
      onCancel={onClose}
    >
      {() => (
        <Form
          showState={false}
          initialValues={{
            name: webhook.name || '',
            url: webhook.url || '',
            ...Object.fromEntries(
              Object.values(WEBHOOK_EVENT).map(event => [
                eventFieldName(event),
                webhook.events?.includes(event) || false,
              ]),
            ),
            status: webhook.status || WEBHOOK_STATUS.ACTIVE,
            customHeaders: formatCustomHeaders(webhook.customHeaders),
          }}
          onSubmit={async values => {
            try {
              // Parse custom headers from JSON string
              let customHeaders = {};
              if (values.customHeaders?.trim()) {
                try {
                  customHeaders = JSON.parse(values.customHeaders);
                } catch (_e) {
                  throw new Error('Invalid JSON format for custom headers');
                }
              }

              const selectedEvents = selectedWebhookEvents(values);

              if (selectedEvents.length === 0) {
                throw new Error('Please select at least one event type');
              }

              const submitData = {
                name: values.name,
                url: values.url,
                events: selectedEvents,
                status: values.status,
              };

              // Include custom headers only if provided
              if (Object.keys(customHeaders).length > 0) {
                submitData.customHeaders = customHeaders;
              }

              const data = await updateWebhook.mutateAsync(submitData);
              return { data };
            } catch (error) {
              return { error };
            }
          }}
          fields={() => [
            {
              name: 'name',
              label: 'Webhook name',
              type: 'text',
              required: true,
              placeholder: 'Payment notifications',
              helpText: 'A descriptive name for this webhook endpoint',
            },
            {
              name: 'url',
              label: 'Endpoint URL',
              type: 'url',
              required: true,
              placeholder: 'https://example.com/webhooks',
              helpText: 'The URL where webhook events will be sent',
            },
            {
              name: 'webhookEvents',
              label: 'Event types',
              type: 'fieldgroup',
              helpText: 'Select which events should trigger this webhook',
              fields: eventOptions.map((option, index) => ({
                name: eventFieldName(option.value),
                label: option.label,
                type: 'checkbox',
                noMargin: index !== eventOptions.length - 1,
              })),
            },
            {
              name: 'status',
              label: 'Status',
              type: 'select',
              required: true,
              options: statusOptions,
              helpText: 'Enable or disable this webhook',
            },
            {
              name: 'customHeaders',
              label: 'Custom headers (optional)',
              type: 'textarea',
              placeholder: '{\n  "Authorization": "Bearer token",\n  "X-Custom-Header": "value"\n}',
              helpText: 'Additional headers to send with webhook requests (JSON format)',
              rows: 4,
            },
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: 'Update webhook',
            appearance: 'primary',
          }}
          cancelButton={{
            label: 'Cancel',
            appearance: 'secondary',
            onClick: onClose,
          }}
        />
      )}
    </Dialog>
  );
};

export default WebhookEditDialog;
